/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useState, useMemo } from "react";
import { Box } from "@nordcloud/gnui";
import { useCostMappingsQuery, CostCategoryMapping } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  ReactTable,
  UniversalWrap,
  TableLoader,
} from "~/components";
import { CostSplittingAlgorithmSelector } from "./CostSplittingAlgorithmSelector";
import { CostSplittingEnvSelector } from "./CostSplittingEnvSelector";
import { costSplittingRulesColumns } from "./CostSplittingRulesList";
import { EnvEditState, CostMappingAlgorithm } from "./types";

export function CostSplittingRulesPage() {
  const [isOpenAllocatedMethodSidebar, setIsOpenAllocatedMethodSidebar] =
    useState(false);
  const [isOpenEnvAllocatedSidebar, setIsOpenEnvAllocatedSidebar] =
    useState(false);

  const [originRowId, setOriginRowId] = useState("");
  const [expandedRow, setExpandedRow] = useState("");

  const [envEdit, setEnvEdit] = useState<CostCategoryMapping | EnvEditState>({
    id: "",
    algorithm: undefined,
    settings: { allEnvironments: false, environments: [] },
  });

  const {
    data: flatData,
    loading,
    error,
    refetch,
  } = useCostMappingsQuery({
    fetchPolicy: "no-cache",
  });

  const onChangeEnvs = useCallback(
    (id: string, rowId: string) => () => {
      setIsOpenEnvAllocatedSidebar(true);
      setExpandedRow(rowId);

      const rule =
        flatData?.costMappings?.find((mapping) => mapping?.id === id) || {};
      setEnvEdit({ ...rule });
    },
    [flatData]
  );

  const onChangeAlgorithm = useCallback(
    (id: string) => () => {
      setIsOpenAllocatedMethodSidebar(true);
      setOriginRowId(id);
    },
    []
  );

  const onChangeEnvsAfterAlgorithm = async (
    id: string,
    algorithm: CostMappingAlgorithm
  ) => {
    await refetch();

    setIsOpenAllocatedMethodSidebar(false);
    setEnvEdit({ ...envEdit, algorithm, id });
    setIsOpenEnvAllocatedSidebar(true);
  };

  const onEnvironmentChanged = async () => {
    setEnvEdit({
      id: "",
      algorithm: undefined,
      settings: { allEnvironments: false, environments: [] },
    });
    await refetch();
    setIsOpenEnvAllocatedSidebar(false);
  };

  const costMappings = flatData?.costMappings?.reduce(
    (
      allRules: Map<string, CostCategoryMapping[]>,
      rule: CostCategoryMapping | null
    ) => {
      const ruleType = rule?.type ?? "";
      allRules.set(ruleType, [...(allRules.get(ruleType) || []), { ...rule }]);

      return allRules;
    },
    new Map()
  );

  const columns = useMemo(
    () => costSplittingRulesColumns({ onChangeAlgorithm, onChangeEnvs }),
    [onChangeAlgorithm, onChangeEnvs]
  );

  const rows = [
    {
      costType: "Discounts",
      icon: "cost",
      subRows: costMappings?.get("discount")?.sort(sortByProvider),
    },
    {
      costType: "Networking cost",
      icon: "network",
      subRows: costMappings?.get("networking")?.sort(sortByProvider),
    },
    {
      costType: "Support Service",
      icon: "webServices",
      subRows: costMappings?.get("support")?.sort(sortByProvider),
    },
    {
      costType: "Other costs",
      icon: "other",
      subRows: costMappings?.get("other")?.sort(sortByProvider),
    },
  ];

  return (
    <>
      <BreadcrumbsBox title="Cost Splitting Rules" />
      <Box spacing="spacing04">
        <UniversalWrap
          loaderProps={{
            loading,
            Component: Loader,
          }}
          errorProps={{
            error,
          }}
        >
          <ReactTable
            initialState={expandRow(expandedRow)}
            columns={columns}
            data={rows}
          />
        </UniversalWrap>
      </Box>
      <CostSplittingAlgorithmSelector
        isOpen={isOpenAllocatedMethodSidebar}
        originRowId={originRowId}
        onClose={() => setIsOpenAllocatedMethodSidebar(false)}
        onSelect={onChangeEnvsAfterAlgorithm}
      />
      <CostSplittingEnvSelector
        isOpen={isOpenEnvAllocatedSidebar}
        isOpenAllocatedMethodSidebar={isOpenAllocatedMethodSidebar}
        algorithm={envEdit?.algorithm ?? undefined}
        value={envEdit.settings ?? { allEnvironments: false, environments: [] }}
        ruleId={envEdit?.id ?? ""}
        onChange={onEnvironmentChanged}
        onClose={() => setIsOpenEnvAllocatedSidebar(false)}
      />
    </>
  );
}

function Loader() {
  return (
    <TableLoader
      gapY={10}
      gapX={6}
      rows={[
        {
          count: 5,
          height: 50,
          items: [
            {
              widthPercent: 15,
            },
            {
              widthPercent: 85,
            },
          ],
        },
      ]}
    />
  );
}

const sortByProvider = (a: CostCategoryMapping, b: CostCategoryMapping) =>
  b.provider
    ?.toLocaleLowerCase()
    .localeCompare(a.provider?.toLocaleLowerCase() ?? "") || -1;

function expandRow(id: string) {
  return {
    expanded: {
      [id]: true,
    },
  };
}
